import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  OnInit,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlContainer, ReactiveFormsModule } from '@angular/forms';
import { AddressType } from '@common';
import { TranslatePipe } from '@i18n';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CheckboxComponent } from '../../../checkbox/components/checkbox/checkbox.component';
import { FormFieldComponent } from '../../../layout/components/form-field/form-field.component';
import { BaseFormGroupComponent } from '../../../shared/components/base-form-group/base-form-group.component';
import { controlValue$ } from '../../../shared/utils/form-control/form-control.utils';
import { AddressTabIndex } from '../../models/address-tab/address-tab-index.enum';
import { FormOverrideAddress } from '../../models/form-override-address/form-override-address.interface';
import { AddressTabsComponent } from '../address-tabs/address-tabs.component';
import { AddressTypeaheadComponent } from '../address-typeahead/address-typeahead.component';
import { ManualAddressComponent } from '../manual-address/manual-address.component';

@Component({
  selector: 'app-form-override-address',
  templateUrl: './override-address.component.html',
  styleUrls: ['./override-address.component.scss'],
  providers: [
    {
      provide: BaseFormGroupComponent,
      useExisting: forwardRef(() => OverrideAddressComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AddressTabsComponent,
    AddressTypeaheadComponent,
    AsyncPipe,
    CheckboxComponent,
    FormFieldComponent,
    ManualAddressComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  standalone: true,
})
export class OverrideAddressComponent
  extends BaseFormGroupComponent<FormOverrideAddress>
  implements OnInit
{
  readonly addressOverrideCheckboxLabel = input(
    'I confirm this is a residential address',
  );
  readonly addressOverrideText = input<string>();
  readonly requiredHidden = input<boolean>();

  activeTabIndex$ = new BehaviorSubject<AddressTabIndex | undefined>(undefined);
  isCommercialAddressSearchResult$!: Observable<boolean>;

  protected override readonly controlContainer = inject(ControlContainer);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.setIsCommercialAddressSearchResult();
    this.setIsOverrideAddressControlState();
  }

  onActiveTabIndexChange(activeTabIndex: AddressTabIndex): void {
    this.activeTabIndex$.next(activeTabIndex);
  }

  private setIsCommercialAddressSearchResult(): void {
    this.isCommercialAddressSearchResult$ = controlValue$<AddressType>(
      this.form.get('type'),
    ).pipe(
      map(
        (addressType: AddressType | undefined) =>
          addressType === AddressType.Commercial,
      ),
    );
  }

  private setIsOverrideAddressControlState(): void {
    combineLatest([this.activeTabIndex$, this.isCommercialAddressSearchResult$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const isOverrideAddressControl = this.form.get('isOverrideAddress');

        isOverrideAddressControl?.setValue(undefined);
        isOverrideAddressControl?.markAsUntouched();
      });
  }
}
